import { useCallback, useEffect, useState } from 'react';

import { api } from '@/api/client';
import { hasStoredSession, setAdmin } from '@/auth/session';
import type { Route } from '@/components/Layout';
import { Layout } from '@/components/Layout';
import { AdminsPage } from '@/pages/AdminsPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DiscountsPage } from '@/pages/DiscountsPage';
import { LoginPage } from '@/pages/LoginPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ShopsPage } from '@/pages/ShopsPage';
import { TestStoresPage } from '@/pages/TestStoresPage';

/**
 * Routing is a `useState`, not React Router.
 *
 * Six screens, one user, no deep links worth having — and an ops dashboard's
 * URLs are not something to encourage bookmarking or sharing. A router here
 * would be more moving parts than the thing it routes.
 */
export function App() {
  const [route, setRoute] = useState<Route>('dashboard');
  const [signedIn, setSignedIn] = useState(false);
  const [checking, setChecking] = useState(true);

  /**
   * ⚠ Resume from a stored refresh token BEFORE showing the login screen.
   *
   * Without this, every reload of a live session shows a sign-in prompt for a
   * moment — which trains the one person who uses this to click through a
   * passkey prompt reflexively, on a page they have not read.
   */
  useEffect(() => {
    let cancelled = false;
    if (!hasStoredSession()) {
      // ⚠ Deferred, not synchronous. React Compiler's `set-state-in-effect`
      // rule is right: a setState in an effect body runs during the commit and
      // cascades an extra render before paint.
      queueMicrotask(() => {
        if (!cancelled) setChecking(false);
      });
      return () => {
        cancelled = true;
      };
    }
    void api<{ id: number; username: string; displayName: string | null }>('/ops/api/me')
      .then((me) => {
        if (cancelled) return;
        setAdmin(me);
        setSignedIn(true);
      })
      .catch(() => {
        // Expired, revoked, or the admin was disabled — the client has already
        // cleared the session, so this falls through to the login screen.
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signOut = useCallback(() => {
    setAdmin(null);
    setSignedIn(false);
    setRoute('dashboard');
  }, []);

  if (checking) return null;
  if (!signedIn) return <LoginPage onSignedIn={() => setSignedIn(true)} />;

  return (
    <Layout route={route} onNavigate={setRoute} onSignOut={signOut}>
      {route === 'dashboard' && <DashboardPage />}
      {route === 'shops' && <ShopsPage />}
      {route === 'admins' && <AdminsPage />}
      {route === 'test-stores' && <TestStoresPage />}
      {route === 'discounts' && <DiscountsPage />}
      {route === 'profile' && <ProfilePage />}
    </Layout>
  );
}
